import { SUBMIT_REQUEST_TO_ADD_OR_EDIT_PRODUCTS_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const GetFaireToHelpAddProductsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’d like help updating your catalog, including adding or editing products, updating images, or enriching products imported via your integration, Faire can help. Follow these steps to make a request, and our team will process it within 5 days:"
          description={{
            text: "Introduction to requesting help adding products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the <BoldFormatting>Add products</BoldFormatting> dropdown."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Choose the <BoldFormatting>Get Faire to help</BoldFormatting> option."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="On the <BoldFormatting>Upload catalog</BoldFormatting> tab, select your request type from the dropdown menu."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Use the <BoldFormatting>Download</BoldFormatting> button to download your current catalog, make any needed updates, and upload the new spreadsheet using the button near the bottom of the pop-up, then select <BoldFormatting>Next</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="On the <BoldFormatting>Upload images</BoldFormatting> tab, choose your sharing preference for your catalog images. You’ll be prompted to upload a zip file or provide a link, depending on your choice."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Sixth step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={7}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Add any useful instructions for our Content team."
              description={{
                text: "Seventh step to request help",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={8}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the <BoldFormatting>Submit</BoldFormatting> button at the bottom of the pop-up modal."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Eighth step to request help",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For more information, visit <LinkFormatting>How to Submit a Catalog Update Request</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Link to article for more information",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(SUBMIT_REQUEST_TO_ADD_OR_EDIT_PRODUCTS_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
